import { useAppDispatch, useAppSelector } from "../store/hooks";
import { wikiAPI } from "../api/wp";
import IView from "../types/view";
import { useEffect, useMemo, useState } from "react";
import { getDates } from "../utils/convert";
import moment from "moment";
import { months } from "../components/common/ViewsFilter";
import { getCurrentYear } from "../utils/content";

// type TConvertedData = {
//     labels: string[],
//     count: number[],
//     abnormalDays: number[],
//     colors: string[],
//     dates: string[]
// }

const currentYear = getCurrentYear();
const redColor = "#d4526e";
const blueColor = "#33b2df";

export default function useGraph() {
  const [isLoading, setIsLoading] = useState(true);
  const options = useAppSelector((state) => state.common.options);
  const searchPage = useAppSelector((state) => state.search.page);
  const [
    getViewsData,
    {
      data: viewsData,
      //   isLoading: isViewsDataLoading,
      isSuccess: isViewsDataSuccess,
      isError,
    },
  ] = wikiAPI.useGetViewsDataMutation();

  const values: IView[] = useAppSelector((state) => state.search.views);

  const isMonthly = () => {
    return (
      (options.revisionsFilterPeriod === "year" ||
        options.revisionsFilterPeriod === "lastYear") &&
      !options.revisionsFilterMonthValue
    );
  };

  const mount = useMemo(() => {
    return options.revisionsFilterPeriod === "last90Days"
      ? values.length / 3
      : values.length;
  }, [values]);

  const totalCount = useMemo(() => {
    return values.reduce((acc, cur) => acc + cur.count, 0);
  }, [values]);

  const totalDaysCount = useMemo(() => {
    let daysCount = values.length;

    return daysCount;
  }, [values]);

  const averagePerDay = useMemo(() => {
    return Math.round(totalCount / totalDaysCount);
  }, [values]);

  useEffect(() => {
    if (!searchPage) return;

    const dates = getDates(
      options.revisionsFilterPeriod,
      options.revisionsFilterValue ?? currentYear
    );

    getViewsData({
      query: searchPage.slug,
      dateFrom: dates.from,
      dateTo: dates.to,
      type: "daily",
    });
  }, [
    options.revisionsFilterPeriod,
    options.revisionsFilterValue,
    options.revisionsFilterMonthValue,
  ]);

  const data = useMemo(() => {
    const labels: string[] = [];
    const count: number[] = [];
    const colors: string[] = [];
    const abnormalDays: number[] = [];
    const dates: string[] = [];
    let i = -1;
    let currentLabel = "";
    const isMonthlyValue: boolean = isMonthly();

    values.forEach((value, index) => {
      const valueDate = moment(value.date, "DD/MM/YYYY");
      const label = isMonthlyValue
        ? valueDate.format("MMM YYYY")
        : valueDate.format("DD MMM YYYY");

      if (
        options.revisionsFilterPeriod === "year" &&
        options.revisionsFilterMonthValue
      ) {
        if (valueDate.month() + 1 === options.revisionsFilterMonthValue) {
          const isAbnormal =
            value.count / averagePerDay > 3 && value.count >= 30;
          labels.push(label);
          count.push(value.count);
          abnormalDays.push(isAbnormal ? 1 : 0);
          colors.push(isAbnormal ? redColor : blueColor);
        }
        return;
      }

      if (currentLabel !== label) {
        dates.push(value.date);
        labels.push(label);
        currentLabel = label;
        count.push(value.count);
        if (value.count / averagePerDay > 3 && value.count >= 30) {
          abnormalDays.push(1);
          colors.push(redColor);
        } else {
          abnormalDays.push(0);
          colors.push(blueColor);
        }
        i++;
      } else {
        count[i] += value.count;
        if (value.count / averagePerDay > 3 && value.count >= 30) {
          abnormalDays[i]++;
        }
        if (abnormalDays[i] > 0) {
          colors[i] = redColor;
        }
      }
    });

    return { labels, count, abnormalDays, colors, dates };
  }, [values]);

  useEffect(() => {
    if (isViewsDataSuccess || isError) setIsLoading(false);
  }, [isViewsDataSuccess, isError]);

  const averagePerPeriod = useMemo(() => {
    return Math.round(totalCount / data.labels.length);
  }, [data]);

  const totalAbnormalDays = useMemo(() => {
    return data.abnormalDays.reduce((acc, cur) => acc + cur, 0);
  }, [data]);

  const totalPerPeriodLabel = useMemo(() => {
    if (options.revisionsFilterPeriod === "last90Days")
      return "the last 90 days";
    if (options.revisionsFilterPeriod === "lastMonth") return "the last month";
    if (options.revisionsFilterPeriod === "lastYear")
      return "the last 12 months";
    if (
      options.revisionsFilterPeriod === "year" &&
      !options.revisionsFilterMonthValue
    ) {
      return "the year " + options.revisionsFilterValue;
    } else {
      return (
        months[options.revisionsFilterMonthValue] +
        " " +
        options.revisionsFilterValue
      );
    }
  }, [values]);

  const abnormalDaysLabel = useMemo(() => {
    if (options.revisionsFilterPeriod === "last90Days") return "last 90 days";
    if (options.revisionsFilterPeriod === "lastMonth") return "last month";
    if (options.revisionsFilterPeriod === "lastYear") return "last 12 months";
    if (
      options.revisionsFilterPeriod === "year" &&
      !options.revisionsFilterMonthValue
    ) {
      return options.revisionsFilterValue;
    } else {
      return (
        months[options.revisionsFilterMonthValue] +
        " " +
        options.revisionsFilterValue
      );
    }
  }, [values]);

  const totalPeriodCount = useMemo(() => {
    return data.count.reduce((acc, cur) => acc + cur, 0);
  }, [data]);

  return {
    isMonthly: isMonthly(),
    mount,
    totalCount,
    totalDaysCount,
    averagePerDay,
    data,
    averagePerPeriod,
    totalAbnormalDays,
    totalPerPeriodLabel,
    abnormalDaysLabel,
    totalPeriodCount,
    isError: isError,
    isLoading: isLoading,
  };
}
